import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plane, Car, Bed, Save, Trash2 } from "lucide-react";
import TimePicker from "../components/TimePicker";
import { useAuth } from "../context/AuthContext";

const empty = {
  title: "",
  guest_person_ids: [],
  extra_guest_names: [],
  primary_contact_email: "",
  arrival_date: "",
  departure_date: "",
  arrival_time: "",
  departure_time: "",
  arrival_flight: "",
  return_flight: "",
  rooms_needed: 2,
  car_required: false,
  arrival_airport: "RMU",
  departure_airport: "RMU",
  car_drop_off_lead_minutes: null,
  cost_category: "family",
  notes: "",
  room_assignments: [],
};

export default function BookingForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [settings, setSettings] = useState(null);
  const [extraName, setExtraName] = useState("");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/people").then((r) => setPeople(r.data)).catch(console.error);
    api.get("/settings").then((r) => setSettings(r.data)).catch(console.error);
    if (isEdit) {
      api.get(`/bookings/${id}`).then((r) => {
        const b = r.data;
        // Permission gate: only admin or stay creator can edit. If not allowed,
        // bounce to the View Stay page with a friendly toast.
        if (user && !user.is_admin && b.created_by && b.created_by !== user.user_id) {
          toast.error("Only an admin or the person who created this stay can edit it.");
          nav(`/bookings/${id}`, { replace: true });
          return;
        }
        setForm({
          title: b.title || "",
          guest_person_ids: b.guest_person_ids || [],
          extra_guest_names: b.extra_guest_names || [],
          primary_contact_email: b.primary_contact_email || "",
          arrival_date: b.arrival_date,
          departure_date: b.departure_date,
          arrival_time: b.arrival_time || "",
          departure_time: b.departure_time || "",
          arrival_flight: b.arrival_flight || "",
          return_flight: b.return_flight || "",
          rooms_needed: b.rooms_needed,
          car_required: b.car_required,
          arrival_airport: b.arrival_airport || "RMU",
          departure_airport: b.departure_airport || "RMU",
          car_drop_off_lead_minutes: b.car_drop_off_lead_minutes ?? null,
          cost_category: b.cost_category,
          notes: b.notes || "",
          room_assignments: b.room_assignments || [],
        });
      }).catch(console.error);
    }
  }, [id, isEdit, user, nav]);

  const togglePerson = (pid) => {
    setForm((f) => ({
      ...f,
      guest_person_ids: f.guest_person_ids.includes(pid)
        ? f.guest_person_ids.filter((x) => x !== pid)
        : [...f.guest_person_ids, pid],
    }));
  };

  const addExtraGuest = () => {
    if (!extraName.trim()) return;
    setForm((f) => ({ ...f, extra_guest_names: [...f.extra_guest_names, extraName.trim()] }));
    setExtraName("");
  };
  const removeExtraGuest = (n) => setForm((f) => ({ ...f, extra_guest_names: f.extra_guest_names.filter((x) => x !== n) }));

  const estCost = () => {
    if (!settings) return null;
    const base = form.cost_category === "family" ? settings.family_rate : settings.friends_rate;
    let total = base + (form.car_required ? settings.car_rate : 0);
    const fee = settings.alicante_fee_per_leg || 0;
    if (form.arrival_airport === "ALC") total += fee;
    if (form.departure_airport === "ALC") total += fee;
    return total;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.arrival_date || !form.departure_date) {
      toast.error("Pick arrival and departure dates");
      return;
    }
    if (form.departure_date <= form.arrival_date) {
      toast.error("Departure must be after arrival");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (isEdit) res = await api.put(`/bookings/${id}`, form);
      else res = await api.post("/bookings", form);
      toast.success(isEdit ? "Stay updated" : "Stay confirmed");
      if (res.data.conflicts?.length > 0) toast.warning(`Heads up: overlaps with ${res.data.conflicts.length} other stay(s)`);
      nav(`/bookings/${res.data.booking_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="booking-form-page">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">{isEdit ? "Editar reserva" : "Nueva reserva"}</p>
        <h1 className="font-serif-villa text-4xl">{isEdit ? "Edit a stay" : "Plan a new stay"}</h1>
      </div>

      <form onSubmit={submit} className="villa-card p-6 sm:p-10 space-y-8">
        <Section title="The stay" subtitle="Who's coming and what to call it">
          <Field label="Title (optional)">
            <input
              data-testid="form-title"
              className={inputCls}
              placeholder="e.g. Easter with the kids"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Field>
          <Field label="Cost category">
            <div className="flex gap-2">
              {["family", "friends"].map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`cost-${c}`}
                  onClick={() => setForm({ ...form, cost_category: c })}
                  className={`px-5 py-2 rounded-full text-sm capitalize transition-colors ${
                    form.cost_category === c
                      ? "bg-[#2B2B2B] text-[#FDFBF7]"
                      : "bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Family & guests" subtitle="Pick from regulars or add ad-hoc names">
          <div className="md:col-span-2 space-y-3">
            <div className="flex flex-wrap gap-2">
              {people.length === 0 && <span className="text-sm text-[#9A9A9A] italic">No saved people yet — add some on the People page.</span>}
              {people.map((p) => {
                const active = form.guest_person_ids.includes(p.person_id);
                return (
                  <button
                    type="button"
                    key={p.person_id}
                    data-testid={`person-${p.person_id}`}
                    onClick={() => togglePerson(p.person_id)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors ${
                      active
                        ? "bg-[#C25E3E] text-[#FDFBF7] border border-[#C25E3E]"
                        : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
                    }`}
                  >
                    {p.name}
                    <span className="ml-2 text-[10px] uppercase tracking-wider opacity-70">{p.relation}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2">
              {form.extra_guest_names.map((n) => (
                <span key={n} className="px-3 py-1.5 rounded-full text-sm bg-[#FBF7EF] border border-[#E8E1D5] flex items-center gap-2">
                  {n}
                  <button type="button" onClick={() => removeExtraGuest(n)} className="text-[#C25E3E] hover:text-[#A84C30]">×</button>
                </span>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                data-testid="extra-guest-input"
                className={inputCls + " flex-1"}
                placeholder="Add another guest name…"
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtraGuest())}
              />
              <button type="button" onClick={addExtraGuest} data-testid="add-extra-guest" className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">
                Add
              </button>
            </div>
          </div>

          <Field label="Primary contact email" hint="For booking confirmation">
            <input
              type="email"
              data-testid="form-contact-email"
              className={inputCls}
              placeholder="name@email.com"
              value={form.primary_contact_email}
              onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
            />
          </Field>
        </Section>

        <Section title="Dates" subtitle="Arrival and departure">
          <Field label="Arrival date">
            <input type="date" data-testid="form-arrival-date" required className={inputCls}
              value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} />
          </Field>
          <Field label="Departure date">
            <input type="date" data-testid="form-departure-date" required className={inputCls}
              value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} />
          </Field>
          <Field label="Arrival time">
            <TimePicker testid="form-arrival-time" className={inputCls}
              value={form.arrival_time} onChange={(v) => setForm({ ...form, arrival_time: v })} />
          </Field>
          <Field label="Departure time">
            <TimePicker testid="form-departure-time" className={inputCls}
              value={form.departure_time} onChange={(v) => setForm({ ...form, departure_time: v })} />
          </Field>
        </Section>

        <Section title="Travel" subtitle={<span className="inline-flex items-center gap-1"><Plane className="w-3 h-3" /> Flights & airports</span>}>
          <Field label="Arrival airport">
            <AirportPicker value={form.arrival_airport} onChange={(v) => setForm({ ...form, arrival_airport: v })} testid="form-arrival-airport" />
          </Field>
          <Field label="Departure airport">
            <AirportPicker value={form.departure_airport} onChange={(v) => setForm({ ...form, departure_airport: v })} testid="form-departure-airport" />
          </Field>
          <Field label="Arrival flight">
            <input data-testid="form-arrival-flight" className={inputCls} placeholder="e.g. BA2640"
              value={form.arrival_flight} onChange={(e) => setForm({ ...form, arrival_flight: e.target.value })} />
          </Field>
          <Field label="Return flight">
            <input data-testid="form-return-flight" className={inputCls} placeholder="e.g. BA2641"
              value={form.return_flight} onChange={(e) => setForm({ ...form, return_flight: e.target.value })} />
          </Field>
        </Section>

        <Section title="The casa" subtitle="Rooms and the family car">
          <Field label="Rooms needed">
            <input type="number" min="1" max="6" data-testid="form-rooms" className={inputCls}
              value={form.rooms_needed} onChange={(e) => setForm({ ...form, rooms_needed: Number(e.target.value) })} />
          </Field>
          <Field label="Car required?">
            <button
              type="button"
              data-testid="form-car-toggle"
              onClick={() => setForm({ ...form, car_required: !form.car_required })}
              className={`px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 transition-colors ${
                form.car_required
                  ? "bg-[#457B9D] text-[#FDFBF7]"
                  : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
              }`}
            >
              <Car className="w-4 h-4" /> {form.car_required ? "Yes — reserved" : "No"}
            </button>
          </Field>
          {form.car_required && (() => {
            const airportDefault = form.departure_airport === "ALC" ? 120 : 75;
            const value = form.car_drop_off_lead_minutes ?? airportDefault;
            const advised = form.departure_airport === "ALC" ? "Alicante: 2 hours" : "Murcia: 1h 15m – 1h 30m";
            return (
              <Field label="Car drop-off — minutes before flight">
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="number"
                    min="0"
                    step="5"
                    data-testid="form-car-dropoff-lead"
                    className={inputCls + " max-w-[120px]"}
                    value={value}
                    onChange={(e) => {
                      const n = e.target.value === "" ? null : Number(e.target.value);
                      setForm({ ...form, car_drop_off_lead_minutes: n });
                    }}
                  />
                  <span className="text-xs text-[#7A7A7A]">
                    minutes · advised for {advised}
                  </span>
                  {form.car_drop_off_lead_minutes != null && form.car_drop_off_lead_minutes !== airportDefault && (
                    <button
                      type="button"
                      data-testid="form-car-dropoff-reset"
                      onClick={() => setForm({ ...form, car_drop_off_lead_minutes: null })}
                      className="text-xs text-[#C25E3E] hover:underline"
                    >
                      Reset to advised
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#9A9A9A] mt-1">Used as the drop-off time on the email to the hire company.</p>
              </Field>
            );
          })()}
        </Section>

        <Section title="Bedrooms" subtitle="Tick which rooms are being used and pick the bed configuration for each">
          <div className="md:col-span-2 space-y-2">
            <RoomAssignments
              rooms={settings?.rooms || []}
              value={form.room_assignments}
              onChange={(ra) => setForm({ ...form, room_assignments: ra })}
            />
          </div>
        </Section>

        <Section title="Notes & special requests" subtitle="Anything the cleaner or family should know">
          <div className="md:col-span-2">
            <textarea
              data-testid="form-notes"
              className={inputCls + " min-h-[120px] resize-y"}
              placeholder="Allergies, late arrival, baby cot, beach gear, etc."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </Section>

        {/* Cost preview */}
        {settings && (settings.cost_visibility || []).includes(form.cost_category) && (
          <div className="rounded-2xl border border-[#E8E1D5] bg-[#FBF7EF] p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="cost-preview">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Estimated contribution</div>
              <div className="font-serif-villa text-3xl mt-1 text-[#2B2B2B]">
                {settings.currency_symbol}{estCost()?.toFixed(0)}
              </div>
              <div className="text-xs text-[#7A7A7A] mt-1">
                Base ({form.cost_category}): {settings.currency_symbol}{form.cost_category === "family" ? settings.family_rate : settings.friends_rate}
                {form.car_required && <> · Car: {settings.currency_symbol}{settings.car_rate}</>}
                {form.arrival_airport === "ALC" && <> · Arr ALC: {settings.currency_symbol}{settings.alicante_fee_per_leg}</>}
                {form.departure_airport === "ALC" && <> · Dep ALC: {settings.currency_symbol}{settings.alicante_fee_per_leg}</>}
              </div>
            </div>
            <div className="text-xs text-[#7A7A7A] max-w-sm">
              For cleaning, maintenance and car upkeep. <em>No payment is collected here</em> — it's just so we know.
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="submit-booking-btn"
            className="villa-btn-primary px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Confirm stay"}
          </button>
          <button
            type="button"
            onClick={() => nav(-1)}
            data-testid="cancel-form-btn"
            className="px-5 py-3 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 text-[#2B2B2B] placeholder:text-[#B5B5B5] focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E] transition-all";

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="mb-4">
        <h3 className="font-serif-villa text-xl">{title}</h3>
        {subtitle && <p className="text-sm text-[#7A7A7A]">{subtitle}</p>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="text-[11px] text-[#9A9A9A] mt-1 block">{hint}</span>}
    </label>
  );
}

function AirportPicker({ value, onChange, testid }) {
  const opts = [
    { code: "RMU", label: "Murcia", sub: "RMU" },
    { code: "ALC", label: "Alicante", sub: "ALC · +£35" },
  ];
  return (
    <div className="flex gap-2">
      {opts.map((o) => (
        <button
          key={o.code}
          type="button"
          data-testid={`${testid}-${o.code}`}
          onClick={() => onChange(o.code)}
          className={`flex-1 px-4 py-2.5 rounded-xl text-sm border transition-colors ${
            value === o.code
              ? "bg-[#2B2B2B] text-[#FDFBF7] border-[#2B2B2B]"
              : "bg-[#FDFBF7] border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
          }`}
        >
          <div>{o.label}</div>
          <div className={`text-[10px] tracking-wider ${value === o.code ? "opacity-80" : "text-[#9A9A9A]"}`}>{o.sub}</div>
        </button>
      ))}
    </div>
  );
}

const STANDARD_CONFIGS = [
  { code: "double_2", label: "Double Bed - 2 People" },
  { code: "double_1", label: "Double Bed - 1 Person" },
  { code: "twin_2", label: "Twin Beds - 2 People" },
  { code: "single_1", label: "Single Bed - 1 Person" },
];
const BUNK_CONFIGS = [
  { code: "bottom", label: "Bunk - Bottom" },
  { code: "top", label: "Bunk - Top" },
  { code: "both", label: "Bunk - Both" },
];

function RoomAssignments({ rooms, value, onChange }) {
  if (!rooms || rooms.length === 0) {
    return (
      <div className="text-xs text-[#9A9A9A] italic">
        No bedrooms set up yet. Ask the admin to add rooms in Settings.
      </div>
    );
  }
  const map = new Map(value.map((r) => [r.room_id, r.config]));
  const toggle = (room_id) => {
    const next = map.has(room_id)
      ? value.filter((r) => r.room_id !== room_id)
      : [...value, { room_id, config: rooms.find((r) => r.room_id === room_id)?.type === "bunk" ? "bottom" : "double_2" }];
    onChange(next);
  };
  const setConfig = (room_id, config) => {
    onChange(value.map((r) => (r.room_id === room_id ? { ...r, config } : r)));
  };
  return (
    <div className="space-y-2" data-testid="rooms-list">
      {rooms.map((r) => {
        const checked = map.has(r.room_id);
        const opts = r.type === "bunk" ? BUNK_CONFIGS : STANDARD_CONFIGS;
        return (
          <div key={r.room_id} className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-[#E8E1D5] bg-[#FBF7EF]">
            <label className="flex items-center gap-2 cursor-pointer min-w-[180px]">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#C25E3E]"
                checked={checked}
                onChange={() => toggle(r.room_id)}
                data-testid={`room-toggle-${r.room_id}`}
              />
              <span className="text-sm text-[#2B2B2B]">{r.name}</span>
            </label>
            {checked && (
              <select
                value={map.get(r.room_id) || ""}
                onChange={(e) => setConfig(r.room_id, e.target.value)}
                data-testid={`room-config-${r.room_id}`}
                className="bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40"
              >
                {opts.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
            )}
          </div>
        );
      })}
    </div>
  );
}

